import { inject, injectable } from 'tsyringe';
import { RecommendationDatastoreService } from '../../datastore-service/recommendation-datastore.service';
import { NoRecommendationsReasons } from '../no-recommendations-reasons.enums';
import {
  conditionType,
  ConditionType,
} from './decorators/condition-type.decorator';
import { ConditionContext } from './interfaces/condition-context.interface';
import { ICondition, MatchResult } from './interfaces/condition.interface';
import { LoggerService } from '../../logger.service';
import { TimerService } from '../../timer.service';
import { kDITOWUtilsWrapper } from '../../../constants/injectionTokens';
import { IOWUtilsWrapper } from '../../../interfaces/ow-wrappers/ow-utils-wrapper.interface';

// -----------------------------------------------------------------------------
@injectable()
@conditionType(ConditionType.Global)
export class GlobalConditionCooldownOwLaunch implements ICondition {
  private readonly logger = LoggerService.getCategory(
    'GlobalConditionCooldownOwLaunch',
  );

  // ---------------------------------------------------------------------------
  public constructor(
    private readonly datastoreService: RecommendationDatastoreService,
    @inject(kDITOWUtilsWrapper)
    private readonly owUtilsWrapper: IOWUtilsWrapper,
    private readonly timerService: TimerService,
  ) {}

  // ---------------------------------------------------------------------------
  // ICondition
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  public get id(): string {
    return this.logger.name;
  }

  // ---------------------------------------------------------------------------
  async match(context: ConditionContext): Promise<MatchResult> {
    const { game } = context;
    const configs = await this.datastoreService.getGlobalConfigurations();
    if (!configs) {
      this.logger.warn('Failed to get server configurations - returning false');
      return [false, NoRecommendationsReasons.FailedToGetServiceConfig];
    }

    const gameConfig = await this.datastoreService.getGame(game.id);

    const owLaunchCooldownExpired: boolean =
      await this.isCooldownMinFromLaunchPassed(
        configs.cooldownFromOverwolfLaunchInMinutes,
        gameConfig.shouldShowOnStart,
      );

    if (!owLaunchCooldownExpired) {
      return [false, NoRecommendationsReasons.CooldownOWLaunch];
    }

    return [true, undefined];
  }

  // ---------------------------------------------------------------------------
  private async isCooldownMinFromLaunchPassed(
    cooldownMins: number,
    showOnStart: boolean,
  ): Promise<boolean> {
    if (isNaN(cooldownMins)) {
      return true;
    }

    const cooldownSeconds = cooldownMins * 60;
    const clientInfo = await this.owUtilsWrapper.getClientInfo();

    const timePassedInSeconds = clientInfo?.uptimeSeconds;

    const matches = timePassedInSeconds >= cooldownSeconds;

    if (showOnStart && !matches) {
      await this.sleepIfCooldownIsYetReached(
        timePassedInSeconds,
        cooldownSeconds,
      );
      return true;
    }

    const msg = `launched: ${Math.floor(
      timePassedInSeconds / 60,
    )} Minutes ago, config: ${cooldownMins}Mins, ret:${matches}`;

    this.logger.info(msg);
    return matches;
  }

  // ---------------------------------------------------------------------------
  private async sleepIfCooldownIsYetReached(
    timePassedInSeconds: number,
    cooldownSeconds: number,
  ) {
    const msg = `launched: ${Math.floor(
      timePassedInSeconds / 60,
    )} Minutes ago, started sleep for ${
      cooldownSeconds - timePassedInSeconds
    } sec`;

    this.logger.info(msg);
    const timeToSleep = (cooldownSeconds - timePassedInSeconds) * 1000;

    await this.timerService.sleep(timeToSleep);

    this.logger.info('Sleep timer has finished');
  }
}
